import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faToggleOn, faToggleOff,
  faSpinner, faSearch, faTimes,
  faHandshake
} from '@fortawesome/free-solid-svg-icons';
import stockService from '../services/stockService';
import { fetchGLAccounts, fetchCostCategories } from '../services/invoicingService';
import { useFlashMessage } from './FlashMessage';
import HMSEmptyState from './ui/HMSEmptyState';
import '../pages/Invoicing/OutgoingInvoice.css';
import './TradeOperationsLayout.css';
import './StockModule.css';

const ServicesTab = () => {
  const { showError, showSuccess } = useFlashMessage();
  const [services, setServices] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    service_type: '',
    description: '',
    gl_account: '',
    cost_category: '',
  });

  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const loadRefData = useCallback(async () => {
    try {
      const [types, gl, cc] = await Promise.all([
        stockService.fetchServiceTypes(),
        fetchGLAccounts(),
        fetchCostCategories(),
      ]);
      setServiceTypes(types || []);
      setGlAccounts(gl.results || gl || []);
      setCostCategories(cc.results || cc || []);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  }, []);

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await stockService.fetchServices({ show_inactive: 'true' });
      setServices(data || []);
    } catch (err) {
      console.error('Failed to load services:', err);
      showError('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { loadRefData(); }, [loadRefData]);
  useEffect(() => { loadServices(); }, [loadServices]);

  const openCreateModal = () => {
    setEditingService(null);
    setFormData({
      service_type: serviceTypes.length ? serviceTypes[0].value : '',
      description: '',
      gl_account: '',
      cost_category: '',
    });
    setShowModal(true);
  };

  const openEditModal = (svc) => {
    setEditingService(svc);
    setFormData({
      service_type: svc.service_type,
      description: svc.description,
      gl_account: svc.gl_account || '',
      cost_category: svc.cost_category || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.service_type || !formData.description.trim()) {
      showError('Service Type and Description are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        service_type: formData.service_type,
        description: formData.description.trim(),
        gl_account: formData.gl_account || null,
        cost_category: formData.cost_category || null,
      };
      if (editingService) {
        await stockService.updateService(editingService.id, payload);
        showSuccess('Service updated successfully.');
      } else {
        await stockService.createService(payload);
        showSuccess('Service created successfully.');
      }
      setShowModal(false);
      loadServices();
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        showError(Object.values(detail).flat().join(', '));
      } else {
        showError('Failed to save service.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (svc) => {
    try {
      await stockService.updateService(svc.id, { is_active: !svc.is_active });
      showSuccess(`Service ${svc.is_active ? 'deactivated' : 'activated'}.`);
      loadServices();
    } catch (err) {
      showError('Failed to update service status.');
    }
  };

  const filtered = services.filter((s) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      s.description.toLowerCase().includes(term) ||
      (s.service_type_display || '').toLowerCase().includes(term) ||
      (s.gl_account_display || '').toLowerCase().includes(term) ||
      (s.cost_category_display || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="services-tab-content">
      <div className="services-tab-header">
        <div className="stock-items-search" style={{ flex: 1, padding: 0, marginBottom: 0 }}>
          <div className="search-header">
            <div className="search-input-group">
              <div className="search-input-container">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by type, description, GL account..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input compact"
                />
                {searchTerm && (
                  <button className="clear-search" onClick={() => setSearchTerm('')}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} size="sm" />
          Add New Service
        </button>
      </div>

      <div className="services-table-wrapper">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading services...</p>
          </div>
        ) : filtered.length === 0 ? (
          <HMSEmptyState
            icon={<FontAwesomeIcon icon={faHandshake} />}
            title={searchTerm ? 'No Results' : 'No Services Yet'}
            hint={searchTerm ? 'No services match your search' : 'Click "Add New Service" to create your first service entry'}
          />
        ) : (
          <table className="invoice-list-table services-table">
            <thead>
              <tr>
                <th>Service Type</th>
                <th>Description</th>
                <th>GL Account</th>
                <th>Cost Category</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((svc) => (
                <tr key={svc.id} className={!svc.is_active ? 'inactive-row' : ''}>
                  <td className="invoice-number-cell">{svc.service_type_display}</td>
                  <td>{svc.description}</td>
                  <td>{svc.gl_account_display || '-'}</td>
                  <td>{svc.cost_category_display || '-'}</td>
                  <td>
                    <span className={`status-badge ${svc.is_active ? 'active' : 'inactive'}`}>
                      {svc.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div
                      className={`action-dropdown ${activeDropdown === svc.id ? 'dropdown-open' : ''}`}
                      ref={activeDropdown === svc.id ? dropdownRef : null}
                    >
                      <button
                        className="gear-button"
                        onClick={(e) => { e.stopPropagation(); toggleDropdown(svc.id); }}
                      >
                        Actions
                      </button>
                      {activeDropdown === svc.id && (
                        <div className="dropdown-menu">
                          <button onClick={() => { setActiveDropdown(null); openEditModal(svc); }}>
                            <FontAwesomeIcon icon={faEdit} /> Edit
                          </button>
                          <hr />
                          <button onClick={() => { setActiveDropdown(null); handleToggleActive(svc); }}>
                            <FontAwesomeIcon icon={svc.is_active ? faToggleOff : faToggleOn} />
                            {svc.is_active ? ' Deactivate' : ' Activate'}
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="md-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()}>
            <div className="md-modal-header">
              <div className="md-modal-header-text">
                <FontAwesomeIcon icon={editingService ? faEdit : faPlus} className="md-modal-header-icon" />
                <h2>{editingService ? 'Edit Service' : 'Add New Service'}</h2>
              </div>
              <button className="md-modal-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="md-modal-body">
              <div className="md-form-group">
                <label className="md-form-label">Service Type <span className="required">*</span></label>
                <select
                  className="md-form-select"
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                >
                  <option value="">-- Select Type --</option>
                  {serviceTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Description <span className="required">*</span></label>
                <input
                  className="md-form-input"
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g. Ocean freight - Far East to Europe"
                />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">GL Account</label>
                <select
                  className="md-form-select"
                  value={formData.gl_account}
                  onChange={(e) => setFormData({ ...formData, gl_account: e.target.value })}
                >
                  <option value="">-- None --</option>
                  {glAccounts.filter(a => a.is_active !== false).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.account_number} - {a.account_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Cost Category</label>
                <select
                  className="md-form-select"
                  value={formData.cost_category}
                  onChange={(e) => setFormData({ ...formData, cost_category: e.target.value })}
                >
                  <option value="">-- None --</option>
                  {costCategories.filter(c => c.is_active !== false).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} - {c.short_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="md-modal-footer">
              <button className="md-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="md-btn-submit" onClick={handleSave} disabled={saving}>
                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : editingService ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesTab;
